/**
 * 게시글 본문의 <b>서식 자리표</b> — 색·굵기·밑줄·형광·구분선.
 *
 * <h2>왜 마크다운도 HTML 도 아닌가</h2>
 * 본문은 지금까지 <b>평문</b>이었고 지금도 평문이다({@code content} 칸은 그대로다). HTML 로
 * 갈아타면 저장 형식이 바뀌어 ① 옛 글 전부를 변환해야 하고 ② 서버에 살균 필터가 생기고
 * ③ 팝업·홈 위젯·앱·메일까지 표시 경로를 전부 다시 만들어야 한다. 자리표는 <b>옛 글에 없으므로
 * 옛 글은 한 글자도 달라지지 않는다</b> — 사진 자리표({@code [[사진1]]})가 이미 그 방식이다.
 *
 * <h2>문법</h2>
 * <pre>
 * 줄 단위 (줄 전체가 이 모양일 때만)
 *   [[구분선]]          가로줄
 *   [[가운데:…]]        가운데 정렬 — [[왼쪽:…]] [[오른쪽:…]] 도 같다
 *   [[들여2:…]]         들여쓰기 (1~4단계) · 정렬과 겹쳐 쓸 수 있다
 *   [[표]] … [[표끝]]   표 — 줄이 행, | 가 칸
 *   [[표정렬:가운데]]   표를 어디에 놓을지 (울타리 맨 앞 줄)
 *   [[너비:30|70]]      열 너비(%) — 끌어서 조절한 표에만 붙는다
 *   [[배경#a1b2c3:…]]   표 칸의 배경 (칸을 통째로 감쌀 때만)
 *   [[가운데:…]]        칸 안에 쓰면 그 칸의 글자 정렬
 *
 * 인라인 (줄 안 아무 데나)
 *   **…**               굵게
 *   [[기울임:…]]        기울임
 *   [[밑줄:…]]          밑줄
 *   [[크기18:…]]        글씨 크기(px)
 *   [[빨강:…]] [[주황:…]] [[초록:…]] [[파랑:…]] [[보라:…]] [[회색:…]] [[검정:…]]
 *   [[형광노랑:…]] [[형광초록:…]] [[형광파랑:…]] [[형광분홍:…]] [[형광주황:…]] [[형광회색:…]]
 *   [[형광:…]]          색 이름을 안 적은 형광펜 = 노랑
 *   [[색#a1b2c3:…]]     이름에 없는 색 — 팔레트에서 직접 고른 값
 *   [[형광#a1b2c3:…]]
 *   [[링크:주소 글자]]  누르면 새 창으로 열리는 글자 — 글자를 비우면 주소가 그대로 글자다
 *   https://…           맨주소는 자리표 없이도 링크로 그린다
 * </pre>
 *
 * 링크의 주소와 글자를 <b>빈칸으로</b> 가르는 이유: 주소에는 {@code :} 가 들어 이름 칸에 못 싣고,
 * {@code |} 는 표의 칸을 가른다. 주소에는 빈칸이 없으니 첫 빈칸이 늘 경계다.
 *
 * 겹쳐 쓸 수 있다 — {@code [[빨강:**중요**]]} 는 빨강 + 굵게다. 같은 갈래끼리는 안쪽이 이긴다.
 *
 * <p>사진 자리표({@code [[사진N]]})는 <b>여기서 다루지 않는다</b>. 그것은 본문을 자르는 일이라
 * ({@code NoticeArticleBody}) 글자 꾸미기와 층이 다르다 — 이 모듈은 이미 잘린 조각을 받는다.
 *
 * <h2>순수 파일이다</h2>
 * JSX 가 없다. 앱({@code mobile})이 {@code @/lib/*} 로 이 파일을 그대로 부르고, 웹과 앱이 각자의
 * 방식으로 그린다({@code RichText.tsx} · {@code mobile/src/ui/RichText.tsx}). 문법을 베껴 가면
 * 색을 하나 더할 때 두 곳을 고쳐야 하고 언젠가 한 곳이 어긋난다.
 */

/**
 * 글자 색 <b>빠른 선택</b> — 팔레트 맨 위에 서는 일곱 개다.
 *
 * <p>여기 없는 색도 쓸 수 있다({@code [[색#a1b2c3:…]]}). 이 목록은 <b>막는 울타리가 아니라
 * 자주 쓰는 것</b>이다 — 「빨강」 하나 칠하려고 매번 색 고르는 창을 여는 것은 일이다.
 */
export const RICH_COLORS = [
  { key: '빨강', color: '#dc2626' },
  { key: '주황', color: '#ea580c' },
  { key: '초록', color: '#15803d' },
  { key: '파랑', color: '#1d4ed8' },
  { key: '보라', color: '#7c3aed' },
  { key: '회색', color: '#64748b' },
  // 본문 기본색(--text-secondary)보다 진하다. 「색을 입힌다」 가 아니라 「더 또렷하게」 쓰는 자리다.
  { key: '검정', color: '#0f172a' },
] as const;

export type RichColorKey = (typeof RICH_COLORS)[number]['key'];

/**
 * 형광펜 <b>빠른 선택</b>. 글자색과 마찬가지로 자유 색({@code [[형광#a1b2c3:…]]})도 된다.
 *
 * <p>진한 색을 골라도 글자가 묻히지 않는다 — 그리는 쪽이 {@link isDarkColor} 로 보고 <b>글자를
 * 흰색으로 뒤집는다</b>. 색 목록을 밝은 것으로 묶어 두는 대신 그 판단을 자동으로 넘겼다.
 */
export const HIGHLIGHT_COLORS = [
  { key: '노랑', color: '#fef08a' },
  { key: '초록', color: '#bbf7d0' },
  { key: '파랑', color: '#bfdbfe' },
  { key: '분홍', color: '#fbcfe8' },
  { key: '주황', color: '#fed7aa' },
  { key: '회색', color: '#e2e8f0' },
] as const;

export type HighlightKey = (typeof HIGHLIGHT_COLORS)[number]['key'];

/** 색 이름을 안 적은 {@code [[형광:…]]} 이 쓰는 색. 손으로 칠 때의 기본값이다. */
const DEFAULT_HIGHLIGHT: HighlightKey = '노랑';

/** 자유 색 자리표가 받는 꼴. {@code #rrggbb} 여섯 자리만 — 세 자리·색 이름·rgb() 는 받지 않는다. */
const HEX = /^#[0-9a-f]{6}$/;

/**
 * 어두운 색인가 — <b>그 위에 검은 글씨를 얹을 수 있는가</b>를 묻는다.
 *
 * <p>형광펜 색을 자유롭게 고르게 하면서 「흰 배경에 흰 글씨」 를 막는 방법은 두 가지다: 고를 수
 * 있는 색을 밝은 것으로 묶거나, <b>고른 색에 맞춰 글자를 뒤집거나</b>. 뒤집는 쪽을 택했다 —
 * 색을 막으면 남색 형광펜을 쓰고 싶은 사람이 쓸 방법이 아예 없다.
 *
 * <p>계산은 밝기(luminance) 근사식이다. 사람 눈은 초록에 제일 민감하고 파랑에 둔해서 셋을 고르게
 * 더하면 노랑과 파랑이 같은 밝기로 나온다 — 가중치가 그 차이를 메운다.
 */
export function isDarkColor(hex: string): boolean {
  if (!HEX.test(hex)) return false;
  const red = parseInt(hex.slice(1, 3), 16);
  const green = parseInt(hex.slice(3, 5), 16);
  const blue = parseInt(hex.slice(5, 7), 16);
  return (red * 299 + green * 587 + blue * 114) / 1000 < 140;
}

/** 자유 색 자리표에 실을 값으로 다듬는다. 꼴이 안 맞으면 {@code null} — 자리표를 만들지 않는다. */
export function normalizeHex(value: string): string | null {
  const hex = value.trim().toLowerCase();
  return HEX.test(hex) ? hex : null;
}

/**
 * 글씨 크기 <b>빠른 선택</b>(px). 본문 기본은 13px 이라 그 언저리를 촘촘히, 제목 쪽은 성글게 둔다.
 *
 * <p>여기 없는 값도 자리표로는 들어온다 — 한글 문서에서 붙여넣으면 그 문서의 크기가 그대로 온다
 * ({@code htmlToRichMarkup}). 이 목록은 <b>손으로 고를 때</b> 세우는 것이다.
 */
export const FONT_SIZES = [10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32] as const;

/**
 * 크기의 위아래 끝.
 *
 * <p>상한이 있는 이유: 한글에서 표지 글자(72pt)를 붙여넣으면 공지 한 줄이 화면 한 폭을 먹고,
 * 홈 팝업은 그 줄 하나로 가득 찬다. 하한은 아예 안 읽히는 크기를 막는다.
 */
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 48;

/**
 * 본문이 그려지는 크기(px) — {@code RichText} 의 기본값과 같아야 한다.
 *
 * <p>붙여넣기가 이 값과 <b>같은 크기</b>에는 자리표를 붙이지 않는 근거다. 붙여 봐야 보이는 결과가
 * 똑같은데 본문만 {@code [[크기13:…]]} 로 뒤덮여, 정작 크게 쓴 제목을 찾을 수 없게 된다.
 */
export const BODY_FONT_SIZE = 13;

/**
 * 본문이 그려지는 글자색 — {@code RichText} 의 {@code --text-secondary} 와 같은 값이어야 한다.
 *
 * <p>편집기의 「색 없애기」 가 되돌리는 색이고, 되읽기가 <b>「색 없음」 으로 보는</b> 색이다.
 * 안 그러면 색을 지웠는데 {@code [[색#475569:…]]} 이 남아, 보이는 것은 같은데 본문만 지저분해진다.
 */
export const BODY_COLOR = '#475569';

/** 한 조각에 걸린 꾸밈. 없는 것은 빠진다 — 그리는 쪽이 `undefined` 를 그냥 무시할 수 있게. */
export type InlineMarks = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** 글자 색(hex). 빠른 선택({@link RICH_COLORS})이든 직접 고른 값이든 여기서는 같은 hex 다. */
  color?: string;
  /** 형광펜 배경(hex). 어두우면 그리는 쪽이 글자를 흰색으로 뒤집는다({@link isDarkColor}). */
  highlight?: string;
  /** 글씨 크기(px). 없으면 그 자리의 본문 크기 그대로다. */
  size?: number;
  /** 링크 주소. {@link normalizeLinkHref} 를 거친 http·https 만 온다. */
  href?: string;
};

/** 같은 꾸밈이 걸린 글자 한 덩이. */
export type InlineSpan = InlineMarks & { text: string };

export type RichAlign = 'left' | 'center' | 'right';

/**
 * 블록 — 글, 가로줄, 표.
 *
 * <p>한때 「소제목」·「강조 박스」 도 있었는데 <b>뺐다</b>(사용자 지시 2026-09-16). 굵게와 색이
 * 이미 있는 자리에서 소제목은 「굵은 글」 을 한 번 더 부르는 이름이고, 강조 박스는 형광펜과
 * 하는 일이 겹친다. 고를 것이 겹치면 사람은 매번 <b>어느 쪽이 맞나</b>를 새로 고민한다.
 *
 * <p>정렬과 들여쓰기는 <b>블록의 속성</b>이지 글자의 속성이 아니다 — 한 줄의 절반만 가운데로
 * 보낼 수는 없다. 그래서 색·굵기와 달리 {@link InlineMarks} 가 아니라 여기 있다.
 */
/**
 * 표의 칸 하나.
 *
 * <p>{@code background} 는 <b>칸의 속성</b>이지 글자의 속성이 아니다 (사용자 지시 2026-09-16).
 * 한글 표의 「셀 음영」 이 오는 자리다 — 한때 이걸 형광펜({@link InlineMarks#highlight})으로 받고
 * 「칸 안이 전부 한 색이면 칸을 칠한다」 고 눈치껏 판단했는데, 그러면 그 칸에 색 안 칠한 글자를
 * 한 자만 덧붙여도 배경이 글자 뒤 색 띠로 주저앉았다.
 */
export type RichCell = { background?: string; align?: RichAlign; spans: InlineSpan[] };

export type RichBlock =
  | { kind: 'text'; align?: RichAlign; indent?: number; spans: InlineSpan[] }
  | { kind: 'divider' }
  /** 표 한 덩이. 줄이 행, `|` 가 칸이다 — 칸 수가 줄마다 달라도 그대로 그린다. */
  | { kind: 'table'; align?: RichAlign; widths?: number[]; rows: RichCell[][] };

/** 줄 단위 자리표 — 줄 <b>전체</b>가 이 모양일 때만 블록이 된다. */
const DIVIDER_LINE = /^\[\[구분선\]\]$/;

/**
 * 표의 시작과 끝.
 *
 * <p><b>울타리로 두는 이유</b>: 「{@code |} 가 있는 줄은 표」 로 하면 「오전 9시 | 오후 3시」 라고
 * 적은 평범한 문장이 어느 날 갑자기 격자가 된다. 여는 줄을 적게 하면 쓰는 사람이 정한 것만 표다.
 *
 * <p>안쪽은 <b>한글에서 표를 복사해 붙인 모양 그대로</b>다 — 줄이 행이고 {@code |} 로 칸을 나눈다.
 * 손으로 치기도 쉽고, 붙여넣기가 만들어 낸 것을 사람이 이어서 고치기도 쉽다.
 */
const TABLE_OPEN = /^\[\[표\]\]$/;
const TABLE_CLOSE = /^\[\[표끝\]\]$/;

/**
 * 열 너비 — 표 울타리 <b>맨 첫 줄</b>에만 선다. 값은 퍼센트고 칸 수만큼 적는다.
 *
 * <p>칸이 아니라 <b>열</b>의 성질이라 칸 자리표로 두지 않았다. 칸마다 적으면 같은 값을 줄 수만큼
 * 되풀이해야 하고, 한 칸만 고쳐도 열이 어긋난다.
 *
 * <p>끌어서 조절하지 않은 표에는 <b>이 줄이 없다</b>. 없으면 칸 수대로 고르게 나눈다 — 손대지
 * 않은 표가 저장할 때마다 `[[너비:50|50]]` 을 얻을 이유가 없다.
 */
const TABLE_WIDTHS = /^\[\[너비:([\d.|\s]+)\]\]$/;

/**
 * <b>표 자체의 정렬</b> — 울타리 맨 앞 줄에 선다.
 *
 * <p>문단 정렬({@link ALIGN_LINE})과 <b>다른 자리표</b>인 이유: 저것은 칸 <b>안의 글</b>을 어디로
 * 붙일지이고, 이것은 <b>표를 어디에</b> 놓을지다. 한글에서도 「문단 정렬」 과 「개체 배치」 는
 * 다른 메뉴다.
 *
 * <p>정렬을 주면 표가 <b>내용 폭으로 줄어든다</b>. 폭을 꽉 채운 표는 왼쪽이든 가운데든 놓인 자리가
 * 같아서, 안 줄이면 눌러도 아무 일도 안 일어나는 단추가 된다.
 */
const TABLE_ALIGN = /^\[\[표정렬:(왼쪽|가운데|오른쪽)\]\]$/;

export function tableAlignToken(align: RichAlign): string {
  return `[[표정렬:${ALIGN_TOKENS.find((option) => option.align === align)?.key ?? '왼쪽'}]]`;
}

/** 끌어서 맞춘 열 너비를 자리표로. 합이 100 이 되게 다듬는다. */
export function tableWidthsToken(percents: number[]): string {
  const total = percents.reduce((sum, value) => sum + value, 0) || 1;
  return `[[너비:${percents.map((value) => Math.round((value / total) * 1000) / 10).join('|')}]]`;
}

/**
 * <b>칸 배경</b> — 표 칸을 통째로 감쌀 때만 뜻이 있다.
 *
 * <p>형광펜과 <b>다른 자리표</b>인 이유(사용자 지시 2026-09-16): 형광펜은 사람이 문장 안의 낱말에
 * 그은 표시이고, 이것은 표를 짤 때 칸에 입힌 색이다. 한글에서도 「글자 음영」 과 「셀 음영」 은
 * 다른 메뉴다. 하나로 합쳐 두면 칸 안에 색 없는 글자 한 자만 덧붙여도 배경이 색 띠로 주저앉는다.
 */
export const CELL_BACKGROUND = /^\[\[배경(#[0-9a-fA-F]{6}):([\s\S]*)\]\]$/;

/** 칸 배경 자리표를 만든다. 붙여넣기와 화면이 규칙을 나눠 갖는다. */
export function cellBackgroundToken(color: string, content: string): string {
  return `[[배경${normalizeHex(color) ?? '#ffffff'}:${content}]]`;
}

/**
 * 표를 새로 세울 때 본문에 박는 뼈대 — <b>빈 격자</b>다 (사용자 지시 2026-09-16).
 *
 * <p>「항목 | 내용」 을 적어 두었다가 뺐다. 예시 글자가 들어 있으면 쓰는 사람이 <b>지우는 일부터</b>
 * 해야 하고, 지우다 만 칸이 그대로 올라간다. 표 모양은 테두리가 이미 말해 준다.
 */
export const TABLE_SKELETON = ['[[표]]', ' |', ' |', '[[표끝]]'].join('\n');

/** 정렬 자리표의 이름. 화면과 붙여넣기가 같은 것을 읽는다. */
export const ALIGN_TOKENS: readonly { key: string; align: RichAlign }[] = [
  { key: '왼쪽', align: 'left' },
  { key: '가운데', align: 'center' },
  { key: '오른쪽', align: 'right' },
];

/**
 * 정렬이 걸린 줄.
 *
 * <p>줄 하나를 통째로 감싸는 꼴이라({@code [[가운데:…]]}) 여러 줄을 가운데로 보내면 줄마다
 * 자리표가 하나씩 붙는다. 이어지는 같은 정렬은 {@link parseRichBlocks} 가 한 덩이로 다시 묶는다 —
 * 안 묶으면 문단 안의 줄 간격이 문단 사이 간격으로 벌어진다.
 */
export const ALIGN_LINE = /^\[\[(왼쪽|가운데|오른쪽):([\s\S]*)\]\]$/;

/**
 * 들여쓴 줄 — {@code [[들여2:…]]}.
 *
 * <p>한글 문서의 「※ 안내」 줄은 대개 본문보다 한 칸 들어가 있다. 그 들여쓰기가 <b>계층</b>을
 * 말하기 때문이다(이 줄은 위 항목에 딸린 설명이다). 공백으로 밀어 두면 화면 폭이 좁아질 때
 * 줄이 접히면서 그 계층이 사라진다 — 자리표로 두면 접혀도 왼쪽 여백이 유지된다.
 */
export const INDENT_LINE = /^\[\[들여([1-9]):([\s\S]*)\]\]$/;

/** 들여쓰기 깊이의 상한. 넷을 넘기면 본문이 들어갈 자리가 없다. */
export const MAX_INDENT = 4;

/** 한 단계가 밀리는 폭(px). 웹과 앱이 같은 값을 쓴다. */
export const INDENT_STEP = 16;

/**
 * 인라인 자리표 하나.
 *
 * <p>내용을 <b>비탐욕</b>으로 끊는 이유({@code *?}): 한 줄에 색 토큰이 둘 있을 때 탐욕이면
 * 첫 {@code [[} 부터 마지막 {@code ]]} 까지가 한 덩이가 되어, 사이의 평범한 글까지 색이 든다.
 */
/** 자리표 이름에 못 들어가는 글자. 이름이 여기서 끊기면 자리표가 아니다(`[[사진1]]` 이 그렇다). */
const NAME_STOP = /[[\]:\n]/;

/** 이름 길이 상한. 이만큼 안에 `:` 가 안 나오면 그냥 대괄호 두 개일 뿐이다. */
const MAX_NAME = 16;

/** 자리표 이름 → 그 이름이 거는 꾸밈. */
function marksOf(raw: string): InlineMarks {
  const name = raw.toLowerCase();
  if (name === '밑줄') return { underline: true };
  if (name === '기울임') return { italic: true };
  if (name.startsWith('크기')) return { size: clampFontSize(Number(name.slice('크기'.length))) };

  if (name.startsWith('형광')) {
    const tone = name.slice('형광'.length);
    // 자유 색(`[[형광#a1b2c3:…]]`) · 이름(`[[형광초록:…]]`) · 짧은 꼴(`[[형광:…]]` = 노랑)
    if (tone.startsWith('#')) return { highlight: tone };
    return { highlight: HIGHLIGHT_COLORS.find((option) => option.key === (tone || DEFAULT_HIGHLIGHT))?.color };
  }

  if (name.startsWith('색#')) return { color: name.slice('색'.length) };

  return { color: RICH_COLORS.find((option) => option.key === name)?.color };
}

/**
 * 팔레트에서 고른 색 → 본문에 박을 자리표의 앞머리.
 *
 * <p>빠른 선택은 <b>이름으로</b> 적는다({@code [[빨강:}). 본문을 눈으로 읽을 때 {@code #dc2626}
 * 보다 「빨강」 이 낫고, 나중에 회사 색을 손보면 이름을 쓴 글은 함께 따라온다.
 */
export function colorTokenPrefix(color: string): string {
  const preset = RICH_COLORS.find((option) => option.color === color);
  return preset ? `[[${preset.key}:` : `[[색${normalizeHex(color) ?? RICH_COLORS[0].color}:`;
}

export function highlightTokenPrefix(color: string): string {
  const preset = HIGHLIGHT_COLORS.find((option) => option.color === color);
  return preset ? `[[형광${preset.key}:` : `[[형광${normalizeHex(color) ?? HIGHLIGHT_COLORS[0].color}:`;
}

export function sizeTokenPrefix(px: number): string {
  return `[[크기${clampFontSize(px)}:`;
}

export function alignTokenPrefix(align: RichAlign): string {
  return `[[${ALIGN_TOKENS.find((option) => option.align === align)?.key ?? '왼쪽'}:`;
}

export function indentTokenPrefix(level: number): string {
  return `[[들여${Math.min(MAX_INDENT, Math.max(1, Math.round(level)))}:`;
}

/** 링크로 받는 주소. http·https 만 — {@code javascript:} 같은 것이 눌리는 글자로 서면 안 된다. */
const LINK_HREF = /^https?:\/\/[^\s<>"]+$/i;

/** 본문에 맨주소로 적힌 것. {@code y} 라 그 자리에서만 잰다. */
const BARE_URL = /https?:\/\/[^\s<>"'[\]]+/iy;

/** 맨주소 끝의 문장부호는 주소가 아니라 문장의 것이다 — 「…/abc.」 의 마침표. */
const URL_TAIL = /[.,;:!?)}」』'"]+$/;

/**
 * 사람이 적은 주소를 링크로 쓸 꼴로 다듬는다. 못 쓰면 {@code null}.
 *
 * <p>{@code www.example.com} 처럼 앞머리 없이 적은 것은 https 를 붙인다 — 청첩장 주소를 받아
 * 적는 사람은 대개 앞머리를 빼고 적는다. 점이 없는 글자는 주소로 보지 않는다.
 */
export function normalizeLinkHref(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  if (/^[a-z][a-z\d+.-]*:/i.test(value)) return LINK_HREF.test(value) ? value : null;
  return /^[^\s/.]+\.[^\s/]+/.test(value) && LINK_HREF.test(`https://${value}`) ? `https://${value}` : null;
}

/** 맨주소인가 — 보이는 글자가 곧 주소면 자리표 없이 둔다(그리는 쪽이 저절로 링크로 그린다). */
export function isBareUrl(text: string): boolean {
  return LINK_HREF.test(text) && !URL_TAIL.test(text);
}

/**
 * 링크 자리표를 씌운다. 앞뒤 공백은 자리표 밖으로 뺀다.
 *
 * <p>보이는 글자가 맨주소면 <b>씌우지 않는다</b>. 주소를 고쳐 친 링크가 옛 주소를 붙든 채 남으면
 * 보이는 주소와 열리는 주소가 달라진다 — 맨주소는 보이는 글자가 곧 주소다.
 */
export function linkMarkup(href: string, inner: string): string {
  const core = inner.trim();
  if (!core || isBareUrl(core)) return inner;

  const lead = inner.slice(0, inner.indexOf(core[0] ?? ''));
  const tail = inner.slice(lead.length + core.length);
  return `${lead}[[링크:${href} ${core}]]${tail}`;
}

/** {@code [[링크:주소 글자]]} 의 안쪽을 읽는다. 주소가 못 쓰는 꼴이면 링크 없이 글자만 남긴다. */
function parseLink(content: string, inherited: InlineMarks): InlineSpan[] {
  const body = content.trimStart();
  const gap = body.search(/\s/);
  const raw = gap < 0 ? body : body.slice(0, gap);
  const label = gap < 0 ? '' : body.slice(gap + 1).trim();
  const href = normalizeLinkHref(raw);
  return parseInline(label || raw, href ? { ...inherited, href } : inherited);
}

/** 읽히는 범위 안으로 깎는다. 자리표가 무엇을 싣고 오든 화면이 무너지지 않아야 한다. */
export function clampFontSize(px: number): number {
  if (!Number.isFinite(px)) return MIN_FONT_SIZE;
  return Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, Math.round(px)));
}

/**
 * 한 덩이의 글 → 꾸밈이 걸린 조각들.
 *
 * <p>겹친 자리표는 <b>재귀</b>로 푼다 — 안쪽을 다시 이 함수에 넣고, 바깥에서 받은 꾸밈을 얹는다.
 * 안쪽 글은 늘 바깥보다 짧으므로 멈춘다. 같은 갈래가 겹치면(빨강 안의 파랑) 안쪽이 이긴다.
 */
export function parseInline(text: string, inherited: InlineMarks = {}): InlineSpan[] {
  const spans: InlineSpan[] = [];
  let plain = '';
  let at = 0;

  const flush = () => {
    if (plain) spans.push({ ...inherited, text: plain });
    plain = '';
  };

  while (at < text.length) {
    if (text.startsWith('**', at)) {
      const end = text.indexOf('**', at + 2);
      if (end > at + 2) {
        flush();
        spans.push(...parseInline(text.slice(at + 2, end), { ...inherited, bold: true }));
        at = end + 2;
        continue;
      }
    }

    const token = readToken(text, at);
    if (token && token.name === '링크') {
      flush();
      spans.push(...parseLink(token.content, inherited));
      at = token.end;
      continue;
    }
    if (token) {
      flush();
      /*
        <b>모르는 이름도 껍데기를 벗긴다.</b> 이름을 잘못 쳤든, 줄 자리표가 표 칸 안에 들어왔든,
        내가 미처 못 넣은 이름이든 — 화면에 `[[형광#d9eaf7:접수번호]]` 가 글자로 찍히면 읽는
        사람에게 그건 오타로 보이고, 그 자리에서 할 수 있는 일이 없다. 사진 자리표가 이미 쓰는
        규칙이다({@code splitByPhotoTokens}).
      */
      spans.push(...parseInline(token.content, { ...inherited, ...marksOf(token.name) }));
      at = token.end;
      continue;
    }

    // 맨주소 — 이미 링크 안이면 또 걸지 않는다(링크 글자로 주소를 적은 경우).
    if (!inherited.href && (text[at] === 'h' || text[at] === 'H')) {
      BARE_URL.lastIndex = at;
      const url = BARE_URL.exec(text)?.[0].replace(URL_TAIL, '');
      const href = url ? normalizeLinkHref(url) : null;
      if (url && href) {
        flush();
        spans.push({ ...inherited, href, text: url });
        at += url.length;
        continue;
      }
    }

    plain += text[at];
    at += 1;
  }

  flush();
  return spans;
}

/**
 * {@code at} 자리에서 시작하는 자리표 하나를 읽는다 — <b>겹친 것을 세어서</b>.
 *
 * <h2>왜 정규식으로 못 하나 (사용자 지적 2026-09-16)</h2>
 * 예전에는 {@code \[\[이름:([\s\S]*?)\]\]} 하나로 잡았다. 비탐욕이라 내용이 <b>첫 번째</b>
 * {@code ]]} 에서 끊긴다 — 그래서 {@code [[크기13:[[형광#d9eaf7:접수번호]]]]} 같은 <b>겹친</b>
 * 자리표는 안쪽이 반토막 나고, 남은 {@code ]]} 와 붙어 화면에 글자로 찍혔다. 한글 문서의 표는
 * 칸마다 크기와 배경을 함께 갖기 때문에 겹치는 것이 예외가 아니라 보통이다.
 *
 * <p>탐욕({@code *})으로 바꾸면 반대로 <b>한 줄에 둘</b>이 있을 때 사이의 평범한 글까지 삼킨다.
 * 세는 수밖에 없다.
 */
function readToken(text: string, at: number): { name: string; content: string; end: number } | null {
  if (!text.startsWith('[[', at)) return null;

  const colon = text.indexOf(':', at + 2);
  if (colon < 0 || colon - (at + 2) < 1 || colon - (at + 2) > MAX_NAME) return null;

  const name = text.slice(at + 2, colon);
  // 이름 안에 대괄호·줄바꿈이 있으면 자리표가 아니다 — `[[사진1]]` 은 `:` 자체가 없어 여기 못 온다.
  if (NAME_STOP.test(name)) return null;

  let depth = 1;
  let scan = colon + 1;
  while (scan < text.length) {
    if (text.startsWith('[[', scan)) {
      depth += 1;
      scan += 2;
      continue;
    }
    if (text.startsWith(']]', scan)) {
      depth -= 1;
      if (depth === 0) return { name, content: text.slice(colon + 1, scan), end: scan + 2 };
      scan += 2;
      continue;
    }
    scan += 1;
  }

  // 닫는 짝이 없다. 자리표로 보지 않고 그냥 글자로 흘린다 — 뒤에 오는 성한 자리표까지 망치지 않는다.
  return null;
}

/**
 * 본문 → 블록 목록.
 *
 * <p><b>줄 단위 자리표가 없는 글은 덩이 하나로 남는다.</b> 문단마다 자르지 않는 이유는, 지금까지
 * 올라간 글이 전부 「한 덩이 + {@code pre-wrap}」 으로 그려져 있어서다 — 여기서 문단을 나누면
 * 옛 글의 줄 간격이 죄다 달라진다.
 */
export function parseRichBlocks(text: string): RichBlock[] {
  const blocks: RichBlock[] = [];
  let buffer: string[] = [];
  let bufferAlign: RichAlign | undefined;
  let bufferIndent: number | undefined;
  /** 표 울타리 안에서 모으는 중인 줄들. {@code null} 이면 표 밖이다. */
  let tableRows: string[] | null = null;

  const flush = () => {
    const chunk = buffer.join('\n').trim();
    const align = bufferAlign;
    const indent = bufferIndent;
    buffer = [];
    bufferAlign = undefined;
    bufferIndent = undefined;
    if (chunk) blocks.push({ kind: 'text', align, indent, spans: parseInline(chunk) });
  };

  const closeTable = () => {
    const lines = (tableRows ?? []).filter((row) => row.trim());
    tableRows = null;

    /*
      울타리 맨 앞의 <b>머리 줄들</b>을 떼어 낸다 — 표 정렬과 열 너비. 순서를 정해 두지 않는 이유는
      둘 다 사람이 아니라 화면이 적는 줄이라, 나중에 하나가 더 생겨도 여기만 늘면 되기 때문이다.
    */
    let align: RichAlign | undefined;
    let widths: number[] | undefined;
    let at = 0;

    for (; at < lines.length; at += 1) {
      const head = lines[at]?.trim() ?? '';

      const aligned = TABLE_ALIGN.exec(head);
      if (aligned) {
        align = ALIGN_TOKENS.find((option) => option.key === aligned[1])?.align;
        continue;
      }

      const sized = TABLE_WIDTHS.exec(head);
      if (!sized) break;

      widths = (sized[1] ?? '')
        .split('|')
        .map((value) => Number(value.trim()))
        .filter((value) => Number.isFinite(value) && value > 0);
    }

    const rows = lines.slice(at).map((row) => row.split('|').map(parseCell));
    if (rows.length > 0) blocks.push({ kind: 'table', align, widths, rows });
  };

  for (const line of text.split('\n')) {
    const trimmed = line.trim();

    if (tableRows) {
      if (TABLE_CLOSE.test(trimmed)) closeTable();
      else tableRows.push(line);
      continue;
    }

    if (TABLE_OPEN.test(trimmed)) {
      flush();
      tableRows = [];
      continue;
    }

    // 짝을 잃은 닫는 줄. 그릴 것이 없으므로 <b>조용히 버린다</b> — 본문에 `[[표끝]]` 이라는
    // 글자가 찍히면 읽는 사람에게는 그것이 오타로 보인다.
    if (TABLE_CLOSE.test(trimmed)) continue;

    if (DIVIDER_LINE.test(trimmed)) {
      flush();
      blocks.push({ kind: 'divider' });
      continue;
    }

    const { align, indent, content } = peelLineTokens(trimmed, line);

    // 정렬·들여쓰기가 바뀌는 자리가 곧 블록의 경계다 — 한 덩이는 한 가지씩만 가질 수 있다.
    if (align !== bufferAlign || indent !== bufferIndent) flush();
    bufferAlign = align;
    bufferIndent = indent;
    buffer.push(content);
  }

  // 울타리를 안 닫고 글이 끝났으면 <b>거기까지를 표로 본다</b>. 닫는 줄 하나를 지웠다고 해서
  // 표가 통째로 날글자가 되면, 고치다 만 화면이 고장 난 것처럼 보인다.
  if (tableRows) closeTable();
  flush();
  return blocks;
}

/**
 * 표 칸 하나를 읽는다 — 배경, 그리고 안의 글.
 *
 * <p>칸 안에 줄 자리표(정렬·들여쓰기)가 들어와 있을 수 있다. 한글 문서의 표는 칸마다 문단 서식을
 * 갖기 때문이다 — 벗기지 않으면 그 껍데기가 <b>칸에 글자로</b> 찍힌다.
 */
function parseCell(raw: string): RichCell {
  const outer = peelLineTokens(raw.trim(), raw.trim());

  const shaded = CELL_BACKGROUND.exec(outer.content);
  if (!shaded) return { align: outer.align, spans: parseInline(outer.content) };

  // 배경 안쪽에도 정렬이 들어 있을 수 있다(`[[배경#…:[[가운데:…]]]]`).
  const inner = peelLineTokens((shaded[2] ?? '').trim(), (shaded[2] ?? '').trim());
  return {
    background: (shaded[1] ?? '').toLowerCase(),
    align: inner.align ?? outer.align,
    spans: parseInline(inner.content),
  };
}

/**
 * 줄을 감싼 자리표를 <b>겹친 채로</b> 벗긴다 — {@code [[가운데:[[들여1:…]]]]} 도 읽는다.
 *
 * <p>정렬과 들여쓰기는 한 줄에 함께 걸릴 수 있고, 둘 중 어느 것이 바깥인지는 <b>누른 순서</b>가
 * 정한다. 그래서 순서를 정해 두고 그것만 읽는 대신, 더 벗길 것이 없을 때까지 돈다.
 */
function peelLineTokens(trimmed: string, original: string): { align?: RichAlign; indent?: number; content: string } {
  let align: RichAlign | undefined;
  let indent: number | undefined;
  let content = trimmed;
  let peeled = true;

  while (peeled) {
    peeled = false;

    const aligned = ALIGN_LINE.exec(content);
    if (aligned) {
      align = ALIGN_TOKENS.find((option) => option.key === aligned[1])?.align;
      content = (aligned[2] ?? '').trim();
      peeled = true;
      continue;
    }

    const indented = INDENT_LINE.exec(content);
    if (indented) {
      indent = Math.min(MAX_INDENT, Number(indented[1]));
      content = (indented[2] ?? '').trim();
      peeled = true;
    }
  }

  // 자리표가 하나도 없었으면 <b>원래 줄</b>을 돌려준다 — 여기서 trim 해 버리면 손으로 맞춘
  // 공백 들여쓰기가 옛 글에서 사라진다.
  return { align, indent, content: align === undefined && indent === undefined ? original : content };
}

/**
 * 자리표를 걷어낸 맨 글자.
 *
 * <p>서식을 그릴 수 없는 자리에 쓴다 — 목록의 한 줄 미리보기, 알림 문구, 검색 대상. 걷어내지
 * 않으면 {@code [[빨강:}  가 글자 그대로 찍히고, 읽는 사람에게는 그것이 <b>오타로 보인다</b>.
 */
export function stripRichMarkup(text: string): string {
  return parseRichBlocks(text)
    .map((block) => {
      if (block.kind === 'divider') return '';
      if (block.kind === 'table') {
        return block.rows.map((row) => row.map((cell) => cell.spans.map((span) => span.text).join('')).join(' | ')).join('\n');
      }
      return block.spans.map((span) => span.text).join('');
    })
    .join('\n')
    .trim();
}
